import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, MapPin, Siren, Plus, Dog, ChevronRight, PawPrint, ScanLine, Dna, Check, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FullPageSpinner, EmptyState } from "@/components/kit";
import { timeAgo } from "@/lib/files";
import DogFormDialog from "@/components/DogFormDialog";

const photoSrc = (p) => (p ? (p.startsWith("data:") || p.startsWith("http") ? p : `data:image/jpeg;base64,${p}`) : null);

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dogs, setDogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [breedIds, setBreedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDog, setShowDog] = useState(false);
  const [savingBreed, setSavingBreed] = useState(null);
  const [savingPhoto, setSavingPhoto] = useState(null);
  const [photoSavedIds, setPhotoSavedIds] = useState([]);

  const saveBreedToDog = async (b, dog) => {
    if (!dog || savingBreed) return;
    setSavingBreed(b.id);
    try {
      const updated = await apiFetch(`/dogs/${dog.id}/breed`, {
        method: "PATCH",
        body: JSON.stringify({ breed: b.primary_breed }),
      });
      setDogs((prev) => prev.map((d) => (d.id === dog.id ? { ...d, breed: updated.breed } : d)));
      toast.success(`Saved "${b.primary_breed}" to ${dog.name}'s profile.`);
    } catch (e) {
      toast.error(e.message || "Could not save breed.");
    } finally {
      setSavingBreed(null);
    }
  };

  const savePhotoToDog = async (b, dog) => {
    if (!dog || savingPhoto || !b.image_base64) return;
    setSavingPhoto(b.id);
    try {
      await apiFetch(`/dogs/${dog.id}/photo`, {
        method: "PATCH",
        body: JSON.stringify({ photo_base64: b.image_base64 }),
      });
      setDogs((prev) => prev.map((d) => (d.id === dog.id ? { ...d, photo_base64: b.image_base64 } : d)));
      setPhotoSavedIds((prev) => [...prev, b.id]);
      toast.success(`Set as ${dog.name}'s profile photo.`);
    } catch (e) {
      toast.error(e.message || "Could not set photo.");
    } finally {
      setSavingPhoto(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const [d, s, b] = await Promise.all([
        apiFetch("/dogs"),
        apiFetch("/chat/sessions").catch(() => []),
        apiFetch("/breed-identify/history").catch(() => []),
      ]);
      setDogs(d || []);
      setSessions(s || []);
      setBreedIds(b || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <FullPageSpinner />;

  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <div>
      <div className="mb-6">
        <p className="text-[#4A4746] font-semibold">Hi, {firstName} 👋</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#2D2B2A]">
          How&apos;s your pup today?
        </h1>
      </div>

      {/* Emergency banner */}
      <Link to="/app/emergency" data-testid="home-emergency-banner">
        <div className="flex items-center gap-4 p-4 rounded-3xl bg-[#D9534F] text-white mb-6 hover:brightness-105 transition-all">
          <div className="h-11 w-11 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
            <Siren size={24} />
          </div>
          <div className="flex-1">
            <p className="font-extrabold">Emergency?</p>
            <p className="text-white/90 text-sm">Find a 24/7 vet and call now.</p>
          </div>
          <ChevronRight />
        </div>
      </Link>

      {/* Snap & Scan */}
      <button
        data-testid="home-scan-btn"
        onClick={() => navigate("/app/scan")}
        className="w-full text-left flex items-center gap-4 p-4 rounded-3xl bg-[#2D2B2A] text-white mb-6 hover:brightness-110 transition-all"
      >
        <div className="h-11 w-11 rounded-2xl bg-[#E86A45] flex items-center justify-center shrink-0">
          <ScanLine size={24} />
        </div>
        <div className="flex-1">
          <p className="font-extrabold">Snap &amp; Scan</p>
          <p className="text-white/80 text-sm">Point your camera at a symptom or a food to check it instantly.</p>
        </div>
        <ChevronRight />
      </button>

      {/* Breed Identifier */}
      <button
        data-testid="home-breed-btn"
        onClick={() => navigate("/app/breed-identifier")}
        className="w-full text-left flex items-center gap-4 p-4 rounded-3xl bg-[#6F8F72] text-white mb-6 hover:brightness-105 transition-all"
      >
        <div className="h-11 w-11 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
          <PawPrint size={24} />
        </div>
        <div className="flex-1">
          <p className="font-extrabold">Breed Identifier</p>
          <p className="text-white/90 text-sm">Not sure what mix your pup is? Snap a photo for an AI breed guess.</p>
        </div>
        <ChevronRight />
      </button>

      {/* Dogs */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl font-extrabold">Your dogs</h2>
        <button
          data-testid="home-add-dog-btn"
          onClick={() => setShowDog(true)}
          className="flex items-center gap-1.5 text-sm font-bold text-[#E86A45]"
        >
          <Plus size={16} /> Add dog
        </button>
      </div>

      {dogs.length === 0 ? (
        <div className="rounded-3xl border border-[#E8E3DA] bg-white mb-8">
          <EmptyState
            icon={Dog}
            title="No dogs yet"
            body="Add your first pup to get personalized health guidance."
            action={
              <button
                data-testid="home-empty-add-dog"
                onClick={() => setShowDog(true)}
                className="rounded-full bg-[#E86A45] text-white font-extrabold px-6 py-3"
              >
                Add your first dog
              </button>
            }
          />
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2 mb-8">
          {dogs.map((d) => (
            <Link
              to={`/app/dogs/${d.id}`}
              key={d.id}
              data-testid={`home-dog-card-${d.id}`}
              className="shrink-0 w-44 rounded-3xl overflow-hidden bg-white border border-[#E8E3DA] hover:shadow-md transition-shadow"
            >
              <div className="h-32 bg-[#F2EFE9] flex items-center justify-center">
                {d.photo_base64 ? (
                  <img src={photoSrc(d.photo_base64)} alt={d.name} className="h-full w-full object-cover" />
                ) : (
                  <PawPrint size={40} color="#C4BFB8" />
                )}
              </div>
              <div className="p-3">
                <p className="font-extrabold truncate">{d.name}</p>
                <p className="text-sm text-[#4A4746] truncate">{d.breed}</p>
                <p className="text-xs text-[#9A938B] mt-0.5">
                  {d.age ? `${d.age} yr` : "—"} · {d.weight ? `${d.weight} ${d.weight_unit === "kg" ? "kg" : "lbs"}` : "—"}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Recently identified */}
      {breedIds.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-extrabold flex items-center gap-2">
              <Dna size={18} color="#6F8F72" /> Recently identified
            </h2>
            <Link to="/app/breed-identifier" className="text-sm font-bold text-[#E86A45]" data-testid="home-breed-seeall">See all</Link>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {breedIds.slice(0, 10).map((b) => {
              const targetDog = dogs.find((d) => d.id === b.dog_id) || dogs[0];
              const isSaved = targetDog && targetDog.breed === b.primary_breed;
              const unclear = !b.primary_breed || b.primary_breed.toLowerCase() === "unclear";
              return (
                <div
                  key={b.id}
                  data-testid={`home-breed-id-${b.id}`}
                  className="shrink-0 w-36 text-left rounded-3xl overflow-hidden bg-white border border-[#E8E3DA] hover:shadow-md transition-shadow flex flex-col"
                >
                  <button
                    onClick={() => navigate("/app/breed-identifier")}
                    data-testid={`home-breed-open-${b.id}`}
                    className="text-left"
                  >
                    <div className="h-28 bg-[#F2EFE9] flex items-center justify-center relative">
                      {b.image_base64 ? (
                        <img src={photoSrc(b.image_base64)} alt={b.primary_breed} className="h-full w-full object-cover" />
                      ) : (
                        <PawPrint size={34} color="#C4BFB8" />
                      )}
                      {b.is_mixed && (
                        <span className="absolute top-2 left-2 text-[10px] font-extrabold uppercase tracking-wide bg-[#6F8F72] text-white px-2 py-0.5 rounded-full">Mix</span>
                      )}
                    </div>
                    <div className="px-3 pt-3">
                      <p className="font-extrabold text-sm truncate">{b.primary_breed}</p>
                      <p className="text-xs text-[#9A938B] mt-0.5">{timeAgo(b.created_at)}</p>
                    </div>
                  </button>
                  {targetDog && !unclear && (
                    <div className="px-3 pb-3 pt-2 mt-auto space-y-1.5">
                      {isSaved ? (
                        <span data-testid={`home-breed-saved-${b.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#6F8F72]">
                          <Check size={13} /> Saved to {targetDog.name}
                        </span>
                      ) : (
                        <button
                          data-testid={`home-breed-save-${b.id}`}
                          onClick={() => saveBreedToDog(b, targetDog)}
                          disabled={savingBreed === b.id}
                          className="w-full rounded-full bg-[#F2EFE9] hover:bg-[#E8E3DA] text-[#2D2B2A] text-xs font-bold py-1.5 px-2 truncate disabled:opacity-60"
                        >
                          {savingBreed === b.id ? "Saving…" : `Save to ${targetDog.name}`}
                        </button>
                      )}
                      {b.image_base64 && (
                        photoSavedIds.includes(b.id) ? (
                          <span data-testid={`home-breed-photo-saved-${b.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#6F8F72]">
                            <Check size={13} /> Photo set
                          </span>
                        ) : (
                          <button
                            data-testid={`home-breed-photo-${b.id}`}
                            onClick={() => savePhotoToDog(b, targetDog)}
                            disabled={savingPhoto === b.id}
                            className="w-full inline-flex items-center justify-center gap-1 rounded-full border border-[#E8E3DA] hover:bg-[#F7F5F1] text-[#6F6A64] text-xs font-bold py-1.5 px-2 truncate disabled:opacity-60"
                          >
                            <ImagePlus size={13} /> {savingPhoto === b.id ? "Saving…" : "Set as photo"}
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <button
          data-testid="home-ask-ai-btn"
          onClick={() => navigate("/app/chat")}
          className="text-left p-6 rounded-3xl bg-[#E86A45] text-white hover:brightness-105 transition-all"
        >
          <MessageCircle size={28} />
          <p className="font-display text-xl font-extrabold mt-3">Ask AI</p>
          <p className="text-white/90 text-sm mt-1">Health questions & photo symptom analysis.</p>
        </button>
        <button
          data-testid="home-find-vets-btn"
          onClick={() => navigate("/app/vets")}
          className="text-left p-6 rounded-3xl bg-[#6F8F72] text-white hover:brightness-105 transition-all"
        >
          <MapPin size={28} />
          <p className="font-display text-xl font-extrabold mt-3">Find a vet</p>
          <p className="text-white/90 text-sm mt-1">Nearby clinics, ratings & 24/7 emergency.</p>
        </button>
      </div>

      {/* Recent chats */}
      {sessions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-extrabold">Recent conversations</h2>
            <Link to="/app/chat" className="text-sm font-bold text-[#E86A45]">See all</Link>
          </div>
          <div className="space-y-2">
            {sessions.slice(0, 5).map((s) => (
              <Link
                to={`/app/chat/${s.id}`}
                key={s.id}
                data-testid={`home-session-${s.id}`}
                className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-[#E8E3DA] hover:shadow-sm transition-shadow"
              >
                <div className={`h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 ${s.is_emergency ? "bg-[#FDECEB]" : "bg-[#F6DED5]"}`}>
                  {s.is_emergency ? <Siren size={18} color="#D9534F" /> : <MessageCircle size={18} color="#E86A45" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{s.title}</p>
                  <p className="text-sm text-[#4A4746] truncate">{s.last_message}</p>
                </div>
                <span className="text-xs text-[#9A938B] shrink-0">{timeAgo(s.updated_at)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <DogFormDialog open={showDog} onClose={() => setShowDog(false)} onSaved={load} />
    </div>
  );
}
